// Logic for the company start page "Att göra nu" list: which action items are
// active, and how important each is. Kept as a pure function so the conditions
// and the order are unit-testable independently of rendering.
//
// Priority (ClickUp 869fj6nkx): urgent = stops the project or the payouts
// (an objection, KYC, bank account, capabilities, VAT number), waiting = the
// client waits for the company (attest a stage, a contract sent back, sign),
// later = nothing waits (invite, create a project, practical information).
// Within a priority the internal order is the array order below.

import type { AdyenAccount } from '../services/api';
import type { TodoPriority } from './todoPriority';
import type { MessageThread } from '../types';
import { objectionTurnFromThread } from './objectionTurn';

export type CompanyTodoId =
  | 'invite'
  | 'createProject'
  | 'kyc'
  | 'bankAccount'
  | 'capabilities'
  | 'vat'
  | 'attest'
  | 'disputed'
  | 'awaitingSignature'
  | 'contractRejected'
  | 'practicalInfo'
  // Projects without a Beställare (ClickUp 869fmxjgt), one row per project.
  | 'addCustomer'
  | 'customerWaiting';

export interface CompanyTodoDef {
  id: CompanyTodoId;
  priority: TodoPriority;
}

// Canonical order: priority, then the internal order.
export const COMPANY_TODO_ORDER: readonly CompanyTodoDef[] = [
  // Brådskande — stoppar projektet eller utbetalningarna
  { id: 'disputed', priority: 'urgent' },
  { id: 'kyc', priority: 'urgent' },
  { id: 'bankAccount', priority: 'urgent' },
  { id: 'capabilities', priority: 'urgent' },
  { id: 'vat', priority: 'urgent' },
  // Väntar på dig — beställaren väntar på hantverkaren
  { id: 'attest', priority: 'waiting' },
  { id: 'contractRejected', priority: 'waiting' },
  { id: 'awaitingSignature', priority: 'waiting' },
  // När du har tid — inget väntar
  { id: 'invite', priority: 'later' },
  { id: 'createProject', priority: 'later' },
  { id: 'practicalInfo', priority: 'later' },
];

export interface CompanyTodoInput {
  customersCount: number;
  projectsCount: number;
  adyenAccount: AdyenAccount | null;
  /** kycStatus === 'passed' || hasRequiredAdyenCapabilities(account) */
  kycApproved: boolean;
  showAdyenOnboarding: boolean;
  vatNumber: string | null | undefined;
  /** Whether the company record has loaded (so we don't flag missing fields early). */
  companyLoaded: boolean;
  practicalInfoComplete: boolean;
  pendingAttestationsCount: number;
  disputedMilestonesCount: number;
  awaitingSignatureCount: number;
  contractRejectedCount: number;
}

/**
 * The active to-do items for a company, in canonical group + internal order.
 * A given item appears only while its condition holds.
 */
export function getActiveCompanyTodos(input: CompanyTodoInput): CompanyTodoDef[] {
  const active = new Set<CompanyTodoId>();

  // Grupp 1
  if (input.customersCount === 0) active.add('invite');
  if (input.projectsCount === 0) active.add('createProject');

  // Grupp 2 — Adyen onboarding + payout readiness
  const account = input.adyenAccount;
  const onboardingDone = !!account?.onboardingCompletedAt;
  if (input.showAdyenOnboarding) {
    if (!input.kycApproved && !onboardingDone) active.add('kyc');
    if (!input.kycApproved && onboardingDone) active.add('capabilities');
    if (onboardingDone && !account?.transferInstrumentId) active.add('bankAccount');
  }
  if (input.companyLoaded && !input.vatNumber?.trim()) active.add('vat');

  // Grupp 3 — pågående projekt
  if (input.pendingAttestationsCount > 0) active.add('attest');
  if (input.disputedMilestonesCount > 0) active.add('disputed');
  if (input.awaitingSignatureCount > 0) active.add('awaitingSignature');
  if (input.contractRejectedCount > 0) active.add('contractRejected');

  // Grupp 4
  if (input.companyLoaded && !input.practicalInfoComplete) active.add('practicalInfo');

  return COMPANY_TODO_ORDER.filter(def => active.has(def.id));
}

export type CompanyObjectionKind = 'disputed' | 'underResolution' | 'conflict';

interface ObjectionStage {
  id: string;
  deliveryStatus?: string | null;
  conflictStartedAt?: string | null;
}

/**
 * The stages where the company has to act on an objection (ClickUp 869fmv7b5),
 * by the same rule as the project page: conflict mode always; a remedy under
 * way (under_resolution) always — the company has to remedy; an open objection
 * (disputed) only when it is the company's turn. Home has no per-stage
 * messages, so the turn comes from the stage's message thread (the side of its
 * latest message; none: the company answers).
 */
export function companyObjectionStages<T extends ObjectionStage>(
  milestones: T[],
  threads: Pick<MessageThread, 'threadType' | 'milestoneId' | 'lastMessage'>[]
): Array<{ milestone: T; kind: CompanyObjectionKind }> {
  return milestones.flatMap((milestone): Array<{ milestone: T; kind: CompanyObjectionKind }> => {
    const status = milestone.deliveryStatus ?? '';
    if (status === 'conflict' || (milestone.conflictStartedAt && ['disputed', 'under_resolution'].includes(status))) {
      return [{ milestone, kind: 'conflict' }];
    }
    if (status === 'under_resolution') return [{ milestone, kind: 'underResolution' }];
    if (status !== 'disputed') return [];
    const thread = threads.find(entry => entry.threadType === 'milestone' && entry.milestoneId === milestone.id);
    return objectionTurnFromThread(thread) === 'company' ? [{ milestone, kind: 'disputed' as const }] : [];
  });
}
